import { useState, useEffect } from 'react';

/**
 * Captures the browser's beforeinstallprompt event so the app can trigger
 * the install dialog at a convenient moment.
 *
 * Returns { canInstall, prompt, dismiss }
 *   canInstall – true when the browser is ready to install
 *   prompt     – call this to show the native install dialog
 *   dismiss    – call this to permanently hide the banner (stored in sessionStorage)
 */
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('pwa-install-dismissed') === '1'
  );

  useEffect(() => {
    function handler(e) {
      e.preventDefault();
      setDeferredPrompt(e);
    }
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Hide banner once the app is installed
  useEffect(() => {
    function onInstalled() { setDeferredPrompt(null); }
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  function prompt() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    deferredPrompt.userChoice.then(() => setDeferredPrompt(null));
  }

  function dismiss() {
    sessionStorage.setItem('pwa-install-dismissed', '1');
    setDismissed(true);
  }

  return {
    canInstall: !!deferredPrompt && !dismissed,
    prompt,
    dismiss,
  };
}
